import { useEffect, useState } from 'react'
import { gql } from '@apollo/client'
import { createStrapiQueryHook } from './createStrapiQueryHook'
import {
  GetHomePage,
  GetHomePageData,
} from '../graphql/queries/__generated__/GetHomePage'
import dynamicProcessors from '../dynamicSectionProcessors'
import { CardTypeSectionFragment } from '../graphql/queries/__generated__/CardTypeSectionFragment'
import { BannerFragment, VideoBlockFragment } from '../queries'

const logger = {
  /**
   * Log informational messages
   * @param {string} message
   */
  info: (message: string): void => {
    // Replace with appropriate logging mechanism
  },

  /**
   * @param {string} message The error message
   * @param {Error|any} [err] Optional error object
   */
  error: (message: string, err?: Error | any): void => {
    // Replace with appropriate error logging
  },

  /**
   * Log warning messages
   * @param {string} message The warning message
   */
  warn: (message: string): void => {
    // Replace with appropriate warning logging
  },
}

export type Slide = {
  title: string
  subtitle?: string
  imageUrl?: string
  action?: {
    title: string
    onClick: () => void
  }
  themeBackgroundColor?: string
  themeColor?: string
  titleColorOverride?: string
  buttonColorOverride?: string
}

export type StoryGreetingCardType = {
  frontImage: string
  isHorizontal: boolean
}

export type StoryCardType = {
  title: string
  description: string
  position: 'center' | 'top' | 'bottom'
}

export type StoryItem = StoryGreetingCardType | StoryCardType

export type HomePageData = {
  slides?: BannerFragment
  stories: StoryItem[]
  bentoSections: CardTypeSectionFragment[]
  videoBlock: VideoBlockFragment[]
}

const operation = GetHomePage()

const useHomePageQuery = createStrapiQueryHook<GetHomePageData>(
  gql(operation.query),
)

export function useHomePage(handleSendFreeCard: () => void) {
  const { data: apiData, isLoading, error: apiError } = useHomePageQuery()
  const [data, setData] = useState<HomePageData>({
    slides: undefined,
    stories: [],
    bentoSections: [],
    videoBlock: [],
  })
  const [error, setError] = useState<string | null>(null)

  const strapiUrl =
    process.env.REACT_APP_STRAPI_URL || 'https://strapi.prod.sendoutcards.com'
  const hasToken = !!process.env.REACT_APP_STRAPI_TOKEN
  /**
   * Adapts GetHomePageData to StrapiResult format
   * This ensures type compatibility with the dynamic section processors
   * Always returns a valid StrapiResult object even when input is null
   */
  function adaptToStrapiResult(
    data: GetHomePageData | null,
  ): import('../dynamicSectionProcessors').StrapiResult {
    if (!data || !data.homepage) {
      return {
        homepage: {
          sections: [],
        },
      }
    }

    return {
      homepage: {
        sections:
          data.homepage.sections?.map(section => {
            if (!section) return null
            return section as Record<string, unknown>
          }) || [],
      },
    }
  }

  useEffect(() => {
    if (apiError) {
      const msg =
        apiError instanceof Error ? apiError.message : 'Failed to load homepage'
      logger.error('[Strapi] Homepage error:', msg)
      logger.error('[Strapi] Full error object:', apiError)
      setError(msg)
      return
    }

    if (!apiData || !apiData.homepage) {
      logger.warn('[Strapi] No homepage data returned from API')

      // Removed introspection query that was causing errors in production

      fetch(`${strapiUrl}/graphql`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(hasToken
            ? {
                Authorization: `Bearer ${process.env.REACT_APP_STRAPI_TOKEN}`,
              }
            : {}),
        },
        body: JSON.stringify({
          query: `
          {
            homepage {
              sections {
                __typename
              }
            }
          }
          `,
        }),
      })
        .then(response => {
          return response.json()
        })
        .then(result => {
          if (result.data?.homepage && result.data.homepage.sections) {
            const adaptedData = {
              homepage: {
                sections: result.data.homepage.sections,
              },
            }
            const processedData = mapToHomePageData(
              dynamicProcessors.processSections(adaptedData),
            )
            setData(processedData)
            return
          }
          if (result.errors) {
            logger.error('[Strapi] Homepage query errors:', result.errors)
          }
        })
        .catch(fetchError => {
          logger.error('[Strapi] Homepage test failed:', fetchError)
        })

      setData({
        slides: undefined,
        stories: [],
        bentoSections: [],
        videoBlock: [],
      })
      return
    }

    // Convert GetHomePageData to StrapiResult format
    const strapiData = adaptToStrapiResult(apiData)
    const sections = dynamicProcessors.processSections(strapiData)
    const processedData = mapToHomePageData(sections)
    setData(processedData)
    // eslint-disable-next-line
  }, [apiData, apiError, handleSendFreeCard, isLoading])

  return { data, loading: isLoading, error }
}

function mapToHomePageData(sections: any[] | null): HomePageData {
  if (!sections || !Array.isArray(sections)) {
    return {
      slides: undefined,
      stories: [],
      bentoSections: [],
      videoBlock: [],
    }
  }

  // Use reduce to create a new object for each section without mutations
  return sections.reduce(
    (result: HomePageData, section) => {
      if (!section) return result

      switch (section.__typename) {
        case 'ComponentSectionsBannerSlider': {
          if (section.slides && Array.isArray(section.slides)) {
            // Create new result with updated slides
            return {
              ...result,
              slides: section.slides,
            }
          }
          return result
        }

        case 'ComponentSectionsRibbonStories': {
          if (section.ribbonStory && Array.isArray(section.ribbonStory)) {
            // Create new result with updated stories
            return {
              ...result,
              stories: section.ribbonStory,
            }
          }
          return result
        }

        case 'ComponentSectionsCardTypeSection':
        case 'ComponentSectionsBentoGrid': {
          // Create new result with updated bentoSections array
          return {
            ...result,
            bentoSections: [...result.bentoSections, section],
          }
        }

        case 'ComponentSectionsBentoSections': {
          if (section.bentoSection && Array.isArray(section.bentoSection)) {
            const bentoItems = JSON.parse(JSON.stringify(section.bentoSection))

            // Filter and collect valid bento items
            const validBentoItems = bentoItems.filter((bentoItem: any) => {
              const isValid =
                bentoItem &&
                (bentoItem.__typename === 'ComponentSectionsCardType' ||
                  bentoItem.__typename === 'ComponentSectionsBentoGrid')

              if (!isValid && bentoItem) {
                logger.warn(
                  `[Strapi] Skipping bentoItem due to unexpected __typename: ${bentoItem?.__typename}`,
                )
              }

              return isValid
            })

            // Create new result with updated bentoSections array
            return {
              ...result,
              bentoSections: [...result.bentoSections, ...validBentoItems],
            }
          }

          logger.warn(
            '[Strapi] ComponentSectionsBentoSections found but bentoSection is empty or not an array',
          )
          return result
        }

        case 'ComponentSectionsVideoBlocks': {
          let mutableVideoBlocks = [...result.videoBlock]

          if (section.videoBlock && Array.isArray(section.videoBlock)) {
            // Map each video to its own block and create new array
            const videoBlocks = section.videoBlock.map((video: any) => ({
              ...section,
              videoBlock: [video],
            }))

            mutableVideoBlocks = [...mutableVideoBlocks, ...videoBlocks]
          } else if (section.videoBlock) {
            mutableVideoBlocks = [...mutableVideoBlocks, section]
          }

          return {
            ...result,
            videoBlock: mutableVideoBlocks,
          }
        }

        default:
          return result
      }
    },
    {
      slides: undefined,
      stories: [],
      bentoSections: [],
      videoBlock: [],
    },
  )
}
